/**
 * RenameDialog — small modal for renaming a single file or folder.
 *
 * Server-side this is implemented as a move (CopyObject + DeleteObject)
 * preserving the folder, swapping the basename. Client-side this dialog
 * just collects the new name and constructs the target path.
 *
 * Validation:
 * - Empty rejected.
 * - Slashes rejected (use Move dialog to relocate).
 * - Leading dot rejected (hidden files create discoverability bugs).
 * - Same-as-original is a no-op (no API call).
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

export interface RenameDialogProps {
  open: boolean;
  /** Full source path (e.g., "photos/wedding/IMG_001.jpg"). */
  sourcePath: string;
  /** Hint to the dialog whether the source is a folder. Affects copy. */
  isFolder?: boolean;
  busy?: boolean;
  onCancel: () => void;
  /** Called with the new full path when the admin confirms. */
  onConfirm: (newPath: string) => void;
}

function basename(path: string): string {
  const trimmed = String(path || '').replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  return idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
}

function dirname(path: string): string {
  const trimmed = String(path || '').replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  return idx >= 0 ? trimmed.slice(0, idx) : '';
}

function validate(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'Name is required.';
  if (trimmed.includes('/')) return 'Use Move... to change the parent folder.';
  if (trimmed.startsWith('.')) return 'Names cannot start with a dot.';
  return '';
}

export function RenameDialog({
  open, sourcePath, isFolder, busy, onCancel, onConfirm,
}: RenameDialogProps) {
  const originalName = basename(sourcePath);
  const parent = dirname(sourcePath);

  const [name, setName] = useState(originalName);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(originalName);
      setError('');
    }
  }, [open, originalName]);

  const handleConfirm = () => {
    const trimmed = name.trim();
    const validationErr = validate(trimmed);
    if (validationErr) { setError(validationErr); return; }
    if (trimmed === originalName) { onCancel(); return; }
    const newPath = parent ? `${parent}/${trimmed}` : trimmed;
    onConfirm(newPath);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Rename {isFolder ? 'folder' : 'file'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          fullWidth
          label="New name"
          value={name}
          onChange={(e) => { setName(e.target.value); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
          disabled={busy}
          helperText={parent ? `Will live in: /${parent}` : 'In the root folder'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={busy} variant="contained">
          {busy ? 'Renaming...' : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
